import React from 'react';
import { useTranslation } from 'react-i18next';
import { DeleteConfirmDialog } from '@/components/deleteConfirmDialog';
import { ErrorBoundary } from '@/components/errorBoundary';
import {
    SupplierDetailDrawer,
    SupplierFormModal,
    SupplierPaymentModal,
} from './components';
import { SuppliersPageSkeleton } from './SuppliersPageSkeleton';
import { SuppliersPageView } from './SuppliersPageView';
import { useSuppliersPage } from './useSuppliersPage';
import { getNodeTestIdProp } from '@/utils/nodeTestProps';

/**
 * /suppliers page — the supplier-management view. Owns its data hook
 * (`useSuppliersPage`) and an `ErrorBoundary`; the throw lives in the inner
 * Content child because React boundaries don't catch errors thrown by the
 * component that renders them. The form modal renders outside the boundary so a
 * grid render failure can't take it down. The payment modal and the account
 * drawer sit beside it for the same reason.
 */
export const SuppliersPage: React.FC = () => {
    const page = useSuppliersPage();
    const { t } = useTranslation('pages');

    const copy = React.useMemo(
        () => ({
            errorTitle: t('suppliers.components.supplierTable.errorTitle'),
            errorMessage: t('suppliers.components.supplierTable.errorMessage'),
            errorRetry: t('suppliers.components.supplierTable.errorRetry'),
        }),
        [t],
    );

    return (
        <div
            className="mx-auto flex w-full max-w-7xl flex-col gap-8"
            {...getNodeTestIdProp('suppliers-page')}
            id="suppliers-page"
        >
            <ErrorBoundary
                data-testid="suppliers-page-error-boundary"
                fallbackMessage={copy.errorMessage}
                id="suppliers-page-error-boundary"
                onRetry={page.onRetry}
                resetKeys={[page.error]}
                retryLabel={copy.errorRetry}
                title={copy.errorTitle}
            >
                <SuppliersPageContent page={page} />
            </ErrorBoundary>
            <SupplierFormModal
                error={page.formError}
                initialValues={page.editingInitialValues}
                isOpen={page.isFormOpen}
                isSubmitting={page.isSubmitting}
                mode={page.formMode}
                onClose={page.onCloseForm}
                onSubmit={page.onSubmitSupplier}
            />
            <SupplierPaymentModal
                error={page.paymentError}
                invoices={page.paymentInvoices}
                isOpen={page.isPaymentOpen}
                isSubmitting={page.isPaymentSubmitting}
                onClose={page.onClosePayment}
                onSubmit={page.onSubmitPayment}
                supplierName={page.paymentSupplier?.name ?? ''}
                today={page.today}
            />
            <DeleteConfirmDialog
                confirm={page.deleteConfirm}
                descriptionKey="deleteConfirm.supplierDescription"
                promptKey="deleteConfirm.supplierPrompt"
            />
            <SupplierDetailDrawer
                canDeletePayments={page.canDeletePayments}
                onClose={page.onCloseDetail}
                onRecordPayment={() =>
                    page.detailSupplier &&
                    page.onRecordPayment(page.detailSupplier.id)
                }
                supplier={page.detailSupplier}
            />
        </div>
    );
};

type SuppliersPageContentProps = {
    page: ReturnType<typeof useSuppliersPage>;
};

const SuppliersPageContent: React.FC<SuppliersPageContentProps> = ({
    page,
}) => {
    if (page.error) {
        throw page.error;
    }

    if (page.isLoading || page.data === null) {
        return <SuppliersPageSkeleton />;
    }

    return (
        <SuppliersPageView
            data={page.data}
            onAddSupplier={page.onAddSupplier}
            onDeleteSupplier={page.onDeleteSupplier}
            onEditSupplier={page.onEditSupplier}
            includeArchived={page.includeArchived}
            onPageChange={page.onPageChange}
            onPageSizeChange={page.onPageSizeChange}
            onRecordPayment={page.onRecordPayment}
            onSearchChange={page.onSearchChange}
            onSetArchived={page.onSetArchived}
            onToggleArchived={page.onToggleArchived}
            onViewSupplier={page.onViewSupplier}
            page={page.page}
            pageSize={page.pageSize}
            search={page.search}
            totalCount={page.totalCount}
            totalPages={page.totalPages}
        />
    );
};
